/**
 * Standard Paper Sizes Configuration
 * All dimensions in cm (width x height for portrait orientation)
 */

const PAPER_SIZES = {
    // Custom Poster Sizes - Portrait format PRIMARY (height > width)
    '30x40': { width: 30, height: 40, category: 'Poster' },
    '30x40cm': { width: 30, height: 40, category: 'Poster' },
    '40x60': { width: 40, height: 60, category: 'Poster' },
    '40x60cm': { width: 40, height: 60, category: 'Poster' },
    '60x80': { width: 60, height: 80, category: 'Poster' },
    '60x80cm': { width: 60, height: 80, category: 'Poster' },
    '75x100': { width: 75, height: 100, category: 'Poster' },
    '75x100cm': { width: 75, height: 100, category: 'Poster' },
    // Landscape format FALLBACK (width > height)
    '40x30': { width: 40, height: 30, category: 'Poster' },
    '40x30cm': { width: 40, height: 30, category: 'Poster' },
    '60x40': { width: 60, height: 40, category: 'Poster' },
    '60x40cm': { width: 60, height: 40, category: 'Poster' },
    '80x60': { width: 80, height: 60, category: 'Poster' },
    '80x60cm': { width: 80, height: 60, category: 'Poster' },

    // Commented out - other sizes hidden for now
    /*
    // ISO A Series (most common internationally)
    'A0': { width: 84.1, height: 118.9, category: 'ISO A Series' },
    'A1': { width: 59.4, height: 84.1, category: 'ISO A Series' },
    'A2': { width: 42.0, height: 59.4, category: 'ISO A Series' },
    'A3': { width: 29.7, height: 42.0, category: 'ISO A Series' },
    'A4': { width: 21.0, height: 29.7, category: 'ISO A Series' },
    'A5': { width: 14.8, height: 21.0, category: 'ISO A Series' },
    'A6': { width: 10.5, height: 14.8, category: 'ISO A Series' },

    // ISO B Series
    'B0': { width: 100.0, height: 141.4, category: 'ISO B Series' },
    'B1': { width: 70.7, height: 100.0, category: 'ISO B Series' },
    'B2': { width: 50.0, height: 70.7, category: 'ISO B Series' },
    'B3': { width: 35.3, height: 50.0, category: 'ISO B Series' },
    'B4': { width: 25.0, height: 35.3, category: 'ISO B Series' },
    'B5': { width: 17.6, height: 25.0, category: 'ISO B Series' },

    // North American Sizes
    'Letter': { width: 21.6, height: 27.9, category: 'US/Canada' },
    'Legal': { width: 21.6, height: 35.6, category: 'US/Canada' },
    'Tabloid': { width: 27.9, height: 43.2, category: 'US/Canada' },
    'Ledger': { width: 43.2, height: 27.9, category: 'US/Canada' },

    // Common Poster Sizes (portrait)
    '30x40': { width: 30, height: 40, category: 'Poster' },
    '40x50': { width: 40, height: 50, category: 'Poster' },
    '50x70': { width: 50, height: 70, category: 'Poster' },
    '61x91': { width: 61, height: 91, category: 'Poster' },
    '70x100': { width: 70, height: 100, category: 'Poster' },

    // Square Formats
    '20x20': { width: 20, height: 20, category: 'Square' },
    '30x30': { width: 30, height: 30, category: 'Square' },
    '40x40': { width: 40, height: 40, category: 'Square' },
    '50x50': { width: 50, height: 50, category: 'Square' },
    '60x60': { width: 60, height: 60, category: 'Square' },
    '70x70': { width: 70, height: 70, category: 'Square' },
    */
};

/**
 * Parse size string and return dimensions in cm
 * Supports formats:
 * - "A4" -> { width: 21.0, height: 29.7 }
 * - "30 x 40 cm" -> { width: 30, height: 40 }
 * - "30x40cm" -> { width: 30, height: 40 }
 *
 * @param {string} sizeStr - Size string from UI
 * @param {string} orientation - 'portrait', 'landscape', or 'square'
 * @returns {object|null} - { width, height } in cm or null if invalid
 */
function parsePaperSize(sizeStr, orientation = 'portrait') {
    if (!sizeStr) return null;

    sizeStr = sizeStr.trim();

    // Check if it's a standard paper size name (e.g., "A4", "Letter")
    if (PAPER_SIZES[sizeStr]) {
        let size = { ...PAPER_SIZES[sizeStr] };

        // Apply orientation
        if (orientation === 'landscape' && size.width < size.height) {
            // Swap dimensions for landscape
            [size.width, size.height] = [size.height, size.width];
        } else if (orientation === 'portrait' && size.width > size.height) {
            // Swap dimensions for portrait
            [size.width, size.height] = [size.height, size.width];
        }
        // Square orientation keeps dimensions as-is

        return { widthCm: size.width, heightCm: size.height };
    }

    // Try to parse custom dimension format: "30 x 40 cm", "30x40cm", etc.
    const match = sizeStr.match(/(\d+(?:\.\d+)?)\s*(?:cm)?\s*[xX×]\s*(\d+(?:\.\d+)?)\s*(?:cm)?/);
    if (match) {
        let width = parseFloat(match[1]);
        let height = parseFloat(match[2]);

        // Apply orientation transformation (in case size not in PAPER_SIZES)
        if (orientation === 'landscape' && width < height) {
            [width, height] = [height, width];
        } else if (orientation === 'portrait' && width > height) {
            [width, height] = [height, width];
        } else if (orientation === 'square') {
            // For square, use the smaller dimension
            const minDim = Math.min(width, height);
            width = height = minDim;
        }

        return {
            widthCm: width,
            heightCm: height
        };
    }

    // Try to parse shorthand format: "30x40" (assume the format key itself)
    if (PAPER_SIZES[sizeStr]) {
        let size = { ...PAPER_SIZES[sizeStr] };
        return { widthCm: size.width, heightCm: size.height };
    }

    console.warn('Could not parse size:', sizeStr);
    return null;
}

/**
 * Get display name for a paper size
 * @param {string} sizeKey - Paper size key (e.g., "A4", "30x40")
 * @returns {string} - Display name with dimensions
 */
function getPaperSizeDisplay(sizeKey) {
    const size = PAPER_SIZES[sizeKey];
    if (!size) return sizeKey;

    if (size.width === size.height) {
        // Square format
        return `${sizeKey} (${size.width}×${size.height} cm)`;
    } else {
        // Portrait format by default
        return `${sizeKey} (${size.width}×${size.height} cm)`;
    }
}

/**
 * Get all paper sizes by category
 * @returns {object} - Categories with their sizes
 */
function getPaperSizesByCategory() {
    const categories = {};

    for (const [key, value] of Object.entries(PAPER_SIZES)) {
        const category = value.category || 'Other';
        if (!categories[category]) {
            categories[category] = [];
        }
        categories[category].push({
            key: key,
            width: value.width,
            height: value.height,
            display: getPaperSizeDisplay(key)
        });
    }

    return categories;
}

// Export for use in other scripts
if (typeof window !== 'undefined') {
    window.PAPER_SIZES = PAPER_SIZES;
    window.parsePaperSize = parsePaperSize;
    window.getPaperSizeDisplay = getPaperSizeDisplay;
    window.getPaperSizesByCategory = getPaperSizesByCategory;
}
